import { useState } from 'react';
import { useDispatch } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import ApplyPresetFooter from './ApplyPresetFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import * as PresetActions from '../../actions/PresetActions';
import { Flex } from 'foundations-components/components/Flex';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import PresetTile from './PresetTile';
import { H7 } from 'foundations-components/transitional/components/H7';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `theme-previewer.themePresets.${key}`;
const PresetListPanel = ({
  activeThemePresetId,
  onClose,
  presets,
  presetsRequestStatus: __presetsRequestStatus,
  selectedThemePresetId,
  themePath
}) => {
  const dispatch = useDispatch();
  const [isApplyPresetFooterOpen, setIsApplyPresetFooterOpen] = useState(false);
  const [isApplyingPreset, setIsApplyingPreset] = useState(false);

  // Active preset will be shown at top of list and needs to be filtered out from the list
  const generatedPresets = activeThemePresetId ? presets.filter(preset => preset.id !== activeThemePresetId) : presets;
  // TODO: currently the only "Other" preset is the theme default preset
  // So if no preset is selected and theme is using field defaults,
  // the theme default preset will show as "Current Style" and "Other"
  // will be hidden until there are OOTB presets available
  const showOtherPresets = Boolean(activeThemePresetId);
  const clearPreset = () => {
    dispatch(PresetActions.clearPreset());
  };
  const selectPreset = presetId => {
    dispatch(PresetActions.selectPreset(presetId));
  };
  const handleSelectPreset = presetId => {
    setIsApplyPresetFooterOpen(true);
    if (presetId) {
      selectPreset(presetId);
    } else {
      clearPreset();
    }
  };
  const handleApplyPreset = () => {
    setIsApplyingPreset(true);
    dispatch(selectedThemePresetId ? PresetActions.applyPreset(themePath, selectedThemePresetId, Boolean(activeThemePresetId)) : PresetActions.clearActivePreset(themePath)).then(() => {
      setIsApplyingPreset(false);
      onClose();
    }).catch(err => {
      setIsApplyingPreset(false);
      setTimeout(() => {
        throw err;
      });
    });
  };

  // Restore previously selected preset or clear if none was previously selected
  const handleCancel = () => {
    if (selectedThemePresetId !== activeThemePresetId) {
      if (activeThemePresetId) {
        selectPreset(activeThemePresetId);
      } else {
        clearPreset();
      }
    }
    setIsApplyPresetFooterOpen(false);
  };
  const handleCancelAndClose = () => {
    handleCancel();
    onClose();
  };
  return /*#__PURE__*/_jsxs(Flex, {
    blockSize: "100%",
    flexDirection: "column",
    justifyContent: "space-between",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      overflowY: "auto",
      padding: "500",
      children: [/*#__PURE__*/_jsx(View, {
        children: /*#__PURE__*/_jsx(Breadcrumbs, {
          flush: true,
          marginBlockStart: "0",
          marginBlockEnd: "500",
          children: /*#__PURE__*/_jsx(Link, {
            onClick: handleCancelAndClose,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('back')
            })
          })
        })
      }), /*#__PURE__*/_jsxs(View, {
        marginBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(H7, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('currentStyle')
          })
        }), /*#__PURE__*/_jsx(PresetTile, {
          selected: activeThemePresetId ? selectedThemePresetId === activeThemePresetId : !selectedThemePresetId,
          presetId: activeThemePresetId,
          onClick: () => handleSelectPreset(activeThemePresetId),
          themePath: themePath
        })]
      }), generatedPresets.length > 0 && /*#__PURE__*/_jsxs(View, {
        marginBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(H7, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('generatedPresets')
            }), /*#__PURE__*/_jsx(AIBadge, {})]
          })
        }), /*#__PURE__*/_jsx(List, {
          children: generatedPresets.map((preset, index) => /*#__PURE__*/_jsx(ListItem, {
            marginBlockEnd: index === generatedPresets.length - 1 ? '0' : '300',
            children: /*#__PURE__*/_jsx(PresetTile, {
              selected: selectedThemePresetId === preset.id,
              onClick: () => handleSelectPreset(preset.id),
              presetId: preset.id,
              themePath: themePath
            })
          }, index))
        })]
      }), showOtherPresets && /*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsx(H7, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('otherPresets')
          })
        }), /*#__PURE__*/_jsx(List, {
          children: /*#__PURE__*/_jsx(ListItem, {
            marginBlockEnd: "300",
            children: /*#__PURE__*/_jsx(PresetTile, {
              selected: !selectedThemePresetId,
              onClick: () => handleSelectPreset(),
              themePath: themePath
            })
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs(ApplyPresetFooter, {
      isOpen: isApplyPresetFooterOpen,
      onOpenComplete: () => {
        setIsApplyPresetFooterOpen(true);
      },
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        loading: isApplyingPreset,
        onClick: handleApplyPreset,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('apply')
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: handleCancel,
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cancel')
        })
      })]
    })]
  });
};
export default PresetListPanel;